import React, { useRef } from "react"
import { useQRCode } from "next-qrcode"
import dayjs from "dayjs"
import CloseIcon from "components/icons/CloseIcon"
import { useLang } from "hooks/useLang"
import RoundedXIcon from "components/icons/RoundedX"
import { motion } from "framer-motion"
import SAR from "components/Sar/SAR"

interface IProps {
    ticket_end_time: string
    ticket_id: string
    ticket_number: string
    ticket_price: number
    ticket_start_time: string
    ticket_status: string
    ticket_title: string
    ticket_token: string
    event_city: string
    setOpenModal: (value: boolean) => void
    event_title: string
    seat_id: string
    starting_date?: string
}

const TicketModal = ({
    ticket_start_time,
    ticket_end_time,
    ticket_number,
    ticket_status,
    ticket_title,
    ticket_id,
    ticket_price,
    ticket_token,
    event_city,
    setOpenModal,
    event_title,
    seat_id,
}: IProps) => {
    const { t } = useLang()

    // Close modal on click outside
    const bgRef = useRef(null)
    const { Canvas } = useQRCode()
    window.addEventListener("click", (e) => {
        if (e.target === bgRef.current) {
            setOpenModal(false)
        }
    })

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            ref={bgRef}
            className="fixed bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-primary-theme-500 bg-opacity-50 text-xs backdrop-blur-sm dark:text-black md:text-sm"
        >
            <div className="relative flex w-[90%] max-w-[460px] flex-col rounded-md bg-blue-600 p-10 capitalize">
                <div
                    onClick={() => setOpenModal(false)}
                    className="absolute right-2 top-2 flex cursor-pointer items-center justify-center overflow-hidden rounded-full bg-white p-1 text-xl font-bold dark:bg-transparent"
                >
                    <RoundedXIcon />
                </div>

                {/* The top ticket */}
                <div className="rounded-md bg-white">
                    <div className="flex justify-between p-5">
                        {/* Left side */}
                        <div className="flex flex-col justify-between">
                            <div>
                                <h1 className="font-semibold">{t("event")}</h1>
                                <div>{event_title}</div>
                            </div>

                            <div>
                                <div className="font-semibold">{t("date")}</div>
                                <div>
                                    {dayjs(ticket_start_time).format(
                                        "DD-MM-YYYY"
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="font-semibold">{t("time")}</div>
                                <div className="flex items-center gap-2">
                                    {dayjs(ticket_start_time).format("hh:mm A")}
                                    <div>-</div>
                                    {dayjs(ticket_end_time).format("hh:mm A")}
                                </div>
                            </div>
                            <div>
                                <div className="font-semibold">
                                    {t("location")}
                                </div>
                                <div>{t(event_city)}</div>
                            </div>
                        </div>

                        {/* Right side */}
                        <div className="flex flex-col justify-between gap-20 md:gap-28">
                            <div className="flex flex-col gap-2 text-center">
                                <div className="mx-auto h-[95px] w-[95px]">
                                    {dayjs().isBefore(
                                        dayjs(ticket_end_time)
                                    ) && (
                                        <Canvas
                                            text={ticket_token}
                                            options={{
                                                width: 100,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="font-semibold">
                                    {t("price")}
                                </div>
                                <div>
                                    <SAR price={ticket_price} />
                                    {/* {ticket_price} {t("SAR")} */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* The dash and gap ==> */}
                <div className="relative flex h-7 items-center justify-around bg-blue-600 text-center">
                    <span className="absolute -left-5 top-1/2 z-10 block h-[200%] w-10 -translate-y-1/2 rounded-3xl bg-blue-600" />
                    {new Array(30).fill("-").map((item, index) => (
                        <span
                            key={index}
                            className="cursor-default text-base text-white opacity-70"
                        >
                            {item}
                        </span>
                    ))}
                    <span className="absolute -right-5 top-1/2 z-10 block h-[200%] w-10 -translate-y-1/2 rounded-3xl bg-blue-600" />
                </div>

                {/* The bottom ticket */}
                <div className="rounded-md bg-white p-5">
                    <h1 className="mb-2 text-sm font-bold sm:text-base">
                        {ticket_title}
                    </h1>
                    <div className="flex justify-between">
                        {/* Left side */}
                        <div className="flex flex-col gap-2">
                            <div>
                                <div className="font-semibold">{t("date")}</div>
                                <div>
                                    {dayjs(ticket_start_time).format(
                                        "DD-MM-YYYY"
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                {seat_id && (
                                    <>
                                        <h3 className="font-semibold">
                                            {t("zone")}
                                        </h3>
                                        <p>{seat_id}</p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Right side */}
                        <div className="flex flex-col gap-2">
                            <div>
                                <div className="font-semibold">{t("time")}</div>
                                <div className="flex items-center gap-2">
                                    {dayjs(ticket_start_time).format("hh:mm A")}
                                    <div>-</div>
                                    {dayjs(ticket_end_time).format("hh:mm A")}
                                </div>
                            </div>
                            <div>
                                <div className="font-semibold">
                                    {t("location")}
                                </div>
                                <div>{t(event_city)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

export default TicketModal
